import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'validator';
import { isEmpty } from 'lodash';

import { authHelper } from 'modules/auth/helpers';
import { validationHelper } from 'modules/common/helpers';

import { TextInput, Button } from 'modules/common/components';
import { FormConditionsNew } from 'modules/profile/components';
import { StepDots } from 'modules/auth/components';
import { Checkbox } from 'modules/elements/components';

interface ConfidentialFormProps {
  onSubmit: (data) => void;
  registerError?: string;
  loading?: boolean;
  free?: boolean;
}
const ConfidentialForm = ({
  onSubmit,
  registerError,
  loading,
  free,
}: ConfidentialFormProps) => {
  const { t } = useTranslation(['payment', 'common']);

  const [user, setUser] = React.useState({ email: '', password: '' });
  const [errors, setErrors] = React.useState({
    email: '',
    password: '',
    agb: false,
    dataProt: false,
  });
  const [pwValidity, setPwValidity] = React.useState({} as PasswordValidity);
  const [isPasswordTyping, setIsPasswordTyping] = React.useState(false);
  const [agreedAGBs, setAgreedAGBs] = React.useState(false);
  const [agreedDataProtection, setAgreedDataProtection] = React.useState(false);

  React.useEffect(() => {
    setPwValidity(authHelper.getPasswordValidity(user.password));
  }, [user.password]);

  const onChange = (field: string, value) => {
    const newUser = { ...user };

    setErrors((errors) => ({
      ...errors,
      [field]: '',
      agb: false,
      dataProt: false,
    }));

    if (field === 'password' && value) {
      setIsPasswordTyping(true);
    }

    newUser[field] = value;

    setUser(newUser);
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (!isValidRegisterForm()) return;

    if (!agreedAGBs || !agreedDataProtection) {
      if (!agreedAGBs)
        setErrors((errors) => ({
          ...errors,
          agb: true,
        }));
      if (!agreedDataProtection)
        setErrors((errors) => ({
          ...errors,
          dataProt: true,
        }));
      return;
    }

    const signUpData: any = { ...user, email: user.email.toLowerCase() };
    onSubmit(signUpData);
  };

  const isValidRegisterForm = () => {
    const formErrors = {
      email: '',
      password: '',
      agb: false,
      dataProt: false,
    };

    if (!user.email) {
      formErrors.email = t('auth:email_required');
    } else if (!isEmail(user.email)) {
      formErrors.email = t('auth:email_not_valid');
    }

    formErrors.password = authHelper.getPasswordErrorMessages(user.password, t);

    setErrors(formErrors);

    return validationHelper.isEmptyErrorObject(formErrors);
  };

  React.useEffect(() => {
    if (registerError) {
      setUser({ email: '', password: '' });
    }
  }, [registerError]);

  const onPasswordInputBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      setIsPasswordTyping(false);
    }
  };

  const handleAGBsCheckboxClick = () => {
    setAgreedAGBs((prev) => !prev);
    setErrors((errors) => ({
      ...errors,
      agb: false,
    }));
  };

  const handleDataProtectionCheckboxClick = () => {
    setAgreedDataProtection((prev) => !prev);
    setErrors((errors) => ({
      ...errors,
      dataProt: false,
    }));
  };

  const isPasswordInvalid = Object.values(pwValidity).some((el) => !el);

  return (
    <form onSubmit={handleSubmit} className="t-mt-5">
      <fieldset disabled={loading}>
        <TextInput
          name="email"
          label={t('auth:email_label_long')}
          type="email"
          value={user.email}
          onChange={onChange}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange('email', e.target.value.trim())
          }
          placeholder={t('auth:email_placeholder')}
          error={errors.email}
          maxLength={300}
        />

        <TextInput
          name="password"
          label={t('common:password_label')}
          type="password"
          value={user.password}
          onChange={onChange}
          onBlur={onPasswordInputBlur}
          onFocus={() => {
            setIsPasswordTyping(true);
          }}
          placeholder={t('auth:password_placeholder_registration')}
          error={errors.password}
        />
      </fieldset>

      {isPasswordTyping && isPasswordInvalid && (
        <FormConditionsNew pwValidity={pwValidity} />
      )}

      <div className="t-mt-5">
        <div className="t-mt-5 t-flex t-items-center">
          <Checkbox
            checked={agreedAGBs}
            id="AGBs"
            onChange={handleAGBsCheckboxClick}
            isError={errors.agb}
            disabled={loading}
          />
          <div className="typo-theta t-text-beta-700 t-ml-2">
            {t('auth:sign_up_agb_1')}
            <a
              href="https://www.ninebarc.de/terms"
              target="_blank"
              rel="nofollow noreferrer noopener"
              className="u-noUnderline t-text-alpha-600"
            >
              {t('auth:sign_up_agb_2')}
            </a>
            {t('auth:sign_up_agb_3')}
          </div>
        </div>
      </div>

      <div className="t-mt-5">
        <div className="t-mt-5 t-flex t-items-center">
          <Checkbox
            checked={agreedDataProtection}
            id="dataProtection"
            onChange={handleDataProtectionCheckboxClick}
            isError={errors.dataProt}
            disabled={loading}
          />
          <div className="typo-theta t-text-beta-700 t-ml-2">
            {t('auth:sign_up_data_protection_1')}
            <a
              href="https://www.ninebarc.de/terms"
              target="_blank"
              rel="nofollow noreferrer noopener"
              className="u-noUnderline t-text-alpha-600"
            >
              {t('auth:sign_up_data_protection_2')}
            </a>
            {t('auth:sign_up_data_protection_3')}
          </div>
        </div>
      </div>

      <div className="t-flex t-justify-between t-items-center lg:t-pt-5">
        <StepDots step={3} total={3} />
        <Button type="submit" disabled={isEmpty(user)} loading={loading}>
          {t(!!free ? 'auth:register_now' : 'auth:order_with_payment')}
        </Button>
      </div>
    </form>
  );
};

export default ConfidentialForm;
